import { useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import ReactPlayer from 'react-player/vimeo';
import { useTranslation } from 'react-i18next';
import Modal from '@klv/organisms/modal';
import { DARK_BACKGROUNDS, LIGHT_BACKGROUNDS  } from '@klv/shared/utils/variables';

import Buttons from '@klv/molecules/buttons';

import { generateClassName } from '@klv/shared/utils/functions';

import * as styles from './styles/offsetImageHero.module.scss';

import type { IGatsbyImageData } from 'gatsby-plugin-image';
import type { IOffsetImageHero } from './types';
import type { IHero } from '../hero/types';


const backgroundImages = DARK_BACKGROUNDS.concat(LIGHT_BACKGROUNDS);

const getBackgroundColor = (background: IHero['background'] | IHero['backgroundJpeg'] = 'lavenderPoppy') => {
  if (LIGHT_BACKGROUNDS.includes(background)) return 'cotton';
  if (DARK_BACKGROUNDS.includes(background)) return 'charcoal';
  return background;
};

export const OffsetImageHero = ({
  background = 'lavenderPoppy',
  bodyCopy = '',
  buttons = [],
  videoModalHeadline = '',
  className = '',
  headline = '',
  image = {},
  squareVideoPosterImage = {},
  squareVideoUrl = '',
  ...props
}: IOffsetImageHero) => {
  const { t } = useTranslation();

  const [videoReady, setVideoReady] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const hasBackgroundImage = backgroundImages.includes(background);
  const backgroundColor = getBackgroundColor(background);
  const hasImage = !!image?.gatsbyImage;
  const hasVideo = !!squareVideoUrl;
  const hasVideoPosterImage = !!squareVideoPosterImage?.gatsbyImage;
  let modalVideoUrl: string | undefined;
  const formattedButtons = buttons.map(button => {
    if (button.url?.includes('vimeo')) {
      modalVideoUrl = button.url;
      return {
        ...button,
        action: () => setShowVideoModal(true),
        url: '',
      };
    } return button;
  });

  const ButtonOrDiv = !!modalVideoUrl ? 'button' : 'div';
  
  const showModalIfPossible = () => {
    if (modalVideoUrl) {
      setShowVideoModal(true);
    }
  };

  return (
    <div
      className={generateClassName([className, styles.offsetImageHero, styles[background]])}
      style={hasBackgroundImage ? {
        '--background-gradient-url' : `url(/static/hero-gradients/${background}.webp)`,
      } as React.CSSProperties : undefined}
      {...props}
    >
      <div className={generateClassName(['grid', styles.container])}>
        <div className={styles.contentContainer}>
          {headline && <h1 className={generateClassName(['h1', styles.headline])} dangerouslySetInnerHTML={{ __html: headline }} />}
          {bodyCopy && <div className={generateClassName(['p1', styles.bodyCopy])} dangerouslySetInnerHTML={{ __html: bodyCopy }} />}
          <Buttons
            buttons={formattedButtons}
            buttonColor={(backgroundColor !== 'charcoal') ? 'dark' : 'light'}
            className={styles.buttons}
            variants={['primary', 'ghost']}
          />
          {modalVideoUrl && showVideoModal && (
            <Modal
              contentPadding="30"
              close={() => setShowVideoModal(false)}
              show={showVideoModal}
              size="constrained"
              backgroundColor='lightSand'
              isMediaModal
              title={videoModalHeadline || ' '}
            >
              <ReactPlayer
                url={modalVideoUrl}
                className={generateClassName(['react-player', styles.modalVideo])}
                config={{ playerOptions:{ texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage') } } }  
                controls
                playing
                loop={false}
                width="100%"
                height="auto"
              />
            </Modal>
          )}
        </div>
        {hasImage && (
          <div className={generateClassName([styles.mediaContainer, hasVideo && styles.hasVideo])}>
            <ButtonOrDiv className={styles.imageMobile} onClick={showModalIfPossible}>
              <GatsbyImage alt={image?.altText ?? ''} image={image.gatsbyImage as IGatsbyImageData} loading='eager' />
            </ButtonOrDiv>
            <ButtonOrDiv className={styles.imageDesktop} onClick={showModalIfPossible}>
              <GatsbyImage alt={image?.altText ?? ''} image={{ ...image.gatsbyImage, layout: 'fixed' } as IGatsbyImageData} loading='eager' />
            </ButtonOrDiv>
            {squareVideoUrl && (
              <ButtonOrDiv className={styles.videoContainer} onClick={showModalIfPossible}>
                {hasVideoPosterImage && (
                  <GatsbyImage 
                    alt={squareVideoPosterImage?.altText ?? ''}
                    className={generateClassName([styles.poster, videoReady && styles.videoReady])}
                    image={squareVideoPosterImage.gatsbyImage as IGatsbyImageData}
                    loading='eager'
                  />
                )}
                <ReactPlayer
                  className={styles.video}
                  height='100%'
                  loop={true}
                  muted={true}
                  onReady={() => setVideoReady(true)}
                  playing={true}
                  url={squareVideoUrl}
                  volume={0}
                  width='100%'
                />
              </ButtonOrDiv>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
